(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let revealObserver;

  function getRevealObserver() {
    if (revealObserver || reducedMotion || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(
      (entries, io) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.06, rootMargin: '0px 0px -7% 0px' }
    );

    return revealObserver;
  }

  function initScrollReveals(root = document) {
    if (reducedMotion || !('IntersectionObserver' in window)) return;

    const main = document.getElementById('MainContent');
    if (!main) return;

    const observer = getRevealObserver();
    if (!observer) return;

    const aboveFold = (element) => element.getBoundingClientRect().top < window.innerHeight * 0.93;

    const componentSelector = [
      '.accordion .details',
      '.accordion accordion-custom',
      '.product-details > .group-block',
      '.product-details > product-form-component',
      '.product-details > variant-picker',
      '.contact-form',
    ].join(',');

    const prepareDescendants = (section) => {
      Array.from(section.querySelectorAll('product-card, .resource-card')).forEach((card, index) => {
        if (!(card instanceof HTMLElement) || card.dataset.hoodratMotionBound === 'true') return;
        card.dataset.hoodratMotionBound = 'true';
        card.classList.add('hoodrat-card-reveal');
        card.style.setProperty('--hr-card-delay', `${(index % 4) * 55}ms`);
      });

      Array.from(section.querySelectorAll(componentSelector)).forEach((component, index) => {
        if (!(component instanceof HTMLElement) || component.dataset.hoodratMotionBound === 'true') return;
        component.dataset.hoodratMotionBound = 'true';
        component.classList.add('hoodrat-component-reveal');
        component.style.setProperty('--hr-card-delay', `${(index % 3) * 45}ms`);
      });
    };

    /* Observe only the large section wrappers. Descendant card/component animations follow the section's
       visibility class in CSS, preserving the choreography while removing dozens of observer targets/reflow checks. */
    Array.from(main.children).forEach((section, index) => {
      if (!(section instanceof HTMLElement) || section.hidden || section.matches('script, style, template, noscript')) return;

      prepareDescendants(section);

      if (section.dataset.hoodratSectionMotionBound === 'true') return;
      section.dataset.hoodratSectionMotionBound = 'true';
      section.classList.add('hoodrat-reveal');
      section.style.setProperty('--hr-reveal-delay', `${Math.min(index, 2) * 35}ms`);

      if (aboveFold(section)) section.classList.add('is-visible');
      else observer.observe(section);
    });

    /* Footer is never hidden. We observe the footer shell only and trigger child keyframes when it enters,
       which avoids the desktop content-visibility/opacity deadlock from V3.2. */
    const footer = document.querySelector('footer');
    if (footer instanceof HTMLElement && footer.dataset.hoodratFooterMotionBound !== 'true') {
      footer.dataset.hoodratFooterMotionBound = 'true';
      footer.classList.add('hoodrat-footer-motion');
      if (aboveFold(footer)) footer.classList.add('is-visible');
      else observer.observe(footer);
    }

    document.documentElement.classList.add('hr-motion-ready');
  }

  function enforceAutoplayVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.closest('[id*="section_kXqyRU"]')) return;

    video.controls = false;
    video.removeAttribute('controls');
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');

    const play = () => video.play().catch(() => {});
    if (video.readyState >= 2) play();
    else video.addEventListener('canplay', play, { once: true, passive: true });
  }

  function initCampaignVideo(root = document) {
    const section = root.querySelector?.('[id*="section_kXqyRU"]') || root.closest?.('[id*="section_kXqyRU"]');
    if (!section) return;

    section.querySelectorAll('video').forEach(enforceAutoplayVideo);
    if (section.dataset.hoodratVideoObserver || !('MutationObserver' in window)) return;

    section.dataset.hoodratVideoObserver = 'true';
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('video')) enforceAutoplayVideo(node);
          node.querySelectorAll?.('video').forEach(enforceAutoplayVideo);
        }
      }
    });

    observer.observe(section, { childList: true, subtree: true });
  }

  function initPurchaseButtonShine() {
    if (reducedMotion || document.documentElement.dataset.hoodratShineBound === 'true') return;

    document.documentElement.dataset.hoodratShineBound = 'true';
    const selector = [
      '.buy-buttons-block .add-to-cart-button',
      '.buy-buttons-block .shopify-payment-button__button',
      '.onhow-shine-container .add-to-cart-button',
      '.onhow-shine-container .shopify-payment-button__button',
      '.product-form-buttons button:not(.button-unstyled):not(.quantity-selector *)',
    ].join(',');

    const trigger = (button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.matches(':disabled, [aria-disabled="true"]')) return;

      button.classList.remove('hoodrat-shine-active');
      // Restart only this short click animation; no continuous animation or scroll listener is added.
      void button.offsetWidth;
      button.classList.add('hoodrat-shine-active');
      const onShineEnd = (event) => {
        if (event.target !== button || event.animationName !== 'hoodrat-purchase-shine') return;
        button.classList.remove('hoodrat-shine-active');
        button.removeEventListener('animationend', onShineEnd);
      };
      button.addEventListener('animationend', onShineEnd);
    };

    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest(selector) : null;
        trigger(target);
      },
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target instanceof Element ? event.target.closest(selector) : null;
      trigger(target);
    });
  }

  function init() {
    initScrollReveals();
    initCampaignVideo();
    initPurchaseButtonShine();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initScrollReveals(document.getElementById('MainContent') || document);
    initCampaignVideo(event.target || document);
  });
})();
